import {Component, Input, Output, EventEmitter} from '@angular/core';
import {CommonModule, NgClass} from '@angular/common';
import { AppUtilService } from '@shared/services/app-util.service';
import {MatTooltip} from '@angular/material/tooltip';
@Component({
  selector: 'app-avatar',
  imports: [
    NgClass,
    CommonModule,
    MatTooltip
  ],
  templateUrl: './avatar.component.html',
  standalone: true,
  styleUrl: './avatar.component.css'
})
export class AvatarComponent {
  @Input() maxVisible = 3;
  @Input() size: 'sm' | 'md' | 'lg' | 'xl' | '2xl' = 'sm';
  @Input() charLength: number = 1;
  @Input() userList: { displayName: string; pic?: string | null; projectRole?: string; role?: string }[] = [];
  @Input() customClass: string = 'border-white';
  @Input() showRemaining: boolean = true;
  @Input() showTooltip: boolean = true;

  /** Managers (left); users with projectRole/role MANAGER */
  get managerList() {
    return this.userList.filter(
      (u) => (u.projectRole ?? u.role) === 'MANAGER'
    );
  }
  /** Members (right); users who are not Manager */
  get memberList() {
    return this.userList.filter(
      (u) => (u.projectRole ?? u.role) !== 'MANAGER'
    );
  }
  /** Show role-based layout when we have both managers and members to separate */
  get hasRoleSplit() {
    return this.managerList.length > 0 && this.memberList.length > 0;
  }

  get avatarSizeClass() {
    return {
      'size-[22px]': this.size === 'sm',
      'size-6': this.size === 'md',
      'size-7': this.size === 'lg',
      'size-8': this.size === 'xl',
      'size-10 !text-sm': this.size === '2xl',
    };
  }
  constructor(public appUtilService: AppUtilService){}

  trackByFn(index: number, item: any): any {
    return item.id; // replace 'id' with the unique identifier of each item
  }
  getRemainingNames(users: any[]): string {
    const remaining = users.slice(this.maxVisible);
    const names = remaining.map(u => u.displayName ?? '').filter(Boolean);
    const sorted = this.sortAreasByAlphaNumeric(names);
    return sorted.join(', ');
  }

  /** Alphanumeric sort: letters first (localeCompare), then numbers. */
  private sortAreasByAlphaNumeric(names: string[]): string[] {
    return [...names].sort((a, b) => {
      const lettersA = a.match(/[a-zA-Z]+/)?.[0].toLowerCase() || '';
      const lettersB = b.match(/[a-zA-Z]+/)?.[0].toLowerCase() || '';
      if (lettersA !== lettersB) return lettersA.localeCompare(lettersB);
      const numberA = parseInt(a.match(/\d+/)?.[0] || '0', 10);
      const numberB = parseInt(b.match(/\d+/)?.[0] || '0', 10);
      return numberA - numberB;
    });
  }

  get visibleManagers() {
    return this.managerList.slice(0, this.maxVisible);
  }
  get visibleMembers() {
    return this.memberList.slice(0, this.maxVisible);
  }
  /** Count of managers not shown (for +N on left) */
  get remainingManagersCount() {
    return Math.max(0, this.managerList.length - this.maxVisible);
  }
  /** Count of members not shown (for +N on right) */
  get remainingMembersCount() {
    return Math.max(0, this.memberList.length - this.maxVisible);
  }
  get remainingCount() {
    if (!this.hasRoleSplit) return Math.max(0, this.userList.length - this.maxVisible);
    return Math.max(0, this.userList.length - this.visibleManagers.length - this.visibleMembers.length);
  }
}
